/**
 * Defera Status — single source of truth for tier definitions, thresholds,
 * earn multipliers, theming, and benefits.
 *
 * Used by:
 *  - Database recompute function (mirror these thresholds in SQL)
 *  - Server endpoints (/api/status/:userId)
 *  - Home page Miles card (theming + progress)
 *  - Rewards page (tier matrix + T&Cs)
 *  - StatusBadge / StatusProgressCard components
 *
 * If you change a threshold, multiplier, or benefit here, also update:
 *  - database/migrations/047_create_customer_status.sql (compute_user_tier function)
 *  - database/migrations/049_status_aware_miles_trigger.sql (multiplier table)
 *  - database/migrations/050_birthday_bonus_and_status_benefit_caps.sql (caps + passenger DOB birthday)
 */

export const TIER_IDS = ['member', 'silver', 'gold', 'platinum', 'diamond'];

/**
 * Calendar-year qualifying spend thresholds (USD).
 * QD = SUM(bookings.grand_total_with_hold) where status='completed' AND non-miles payment.
 */
export const TIERS = [
  {
    id: 'member',
    name: 'Member',
    floor: 0,
    multiplier: 1, // base 2 mi/$1 (no boost)
    earnRate: '2 miles per $1',
  },
  {
    id: 'silver',
    name: 'Silver',
    floor: 2000,
    multiplier: 2, // 4 mi/$1 (2× base)
    earnRate: '4 miles per $1',
  },
  {
    id: 'gold',
    name: 'Gold',
    floor: 5000,
    multiplier: 3, // 6 mi/$1 (3× base)
    earnRate: '6 miles per $1',
  },
  {
    id: 'platinum',
    name: 'Platinum',
    floor: 10000,
    multiplier: 4, // 8 mi/$1 (4× base)
    earnRate: '8 miles per $1',
  },
  {
    id: 'diamond',
    name: 'Diamond',
    floor: 15000,
    multiplier: 5, // 10 mi/$1 (5× base)
    earnRate: '10 miles per $1',
  },
];

export const TIER_BY_ID = Object.fromEntries(TIERS.map((t) => [t.id, t]));

/**
 * Per-tier visual theme. Every status-aware surface (Home miles card,
 * StatusBadge, Rewards header) should pull from here so the user sees a
 * consistent identity for their tier across the app.
 *
 * Each theme provides:
 *  - gradient: Tailwind classes for the primary surface gradient (dark-bg-friendly)
 *  - border: subtle border tint
 *  - accent: solid accent color (used for numbers, progress fills)
 *  - text: tier-name text color
 *  - chip: small pill background + text classes for inline badges
 *  - shimmer: optional decorative gradient for the highest tiers
 */
export const TIER_THEME = {
  member: {
    gradient: 'from-zinc-700/20 via-zinc-600/10 to-zinc-700/20',
    border: 'border-white/10',
    accent: 'text-white',
    text: 'text-white/70',
    chip: 'bg-white/10 text-white/80',
    progressFill: 'bg-white/60',
    glow: '',
    /** Comma-separated RGB for SectionBand glass cards. */
    sectionBandRgb: '148,163,184',
  },
  silver: {
    gradient: 'from-slate-300/20 via-zinc-200/10 to-slate-400/20',
    border: 'border-slate-300/30',
    accent: 'text-slate-100',
    text: 'text-slate-200',
    chip: 'bg-slate-300/15 text-slate-100 border border-slate-300/30',
    progressFill: 'bg-gradient-to-r from-slate-300 to-zinc-200',
    glow: 'shadow-[0_0_60px_-15px_rgba(203,213,225,0.3)]',
    sectionBandRgb: '203,213,225',
  },
  gold: {
    gradient: 'from-brand-500/20 via-brand-400/10 to-brand-600/20',
    border: 'border-brand-400/30',
    accent: 'text-brand-300',
    text: 'text-brand-200',
    chip: 'bg-brand-400/15 text-brand-200 border border-brand-400/30',
    progressFill: 'bg-gradient-to-r from-brand-400 to-brand-300',
    glow: 'shadow-[0_0_60px_-15px_rgba(16, 37, 178,0.4)]',
    sectionBandRgb: '10, 18, 51',
  },
  platinum: {
    gradient: 'from-cyan-300/15 via-slate-200/10 to-brand-200/20',
    border: 'border-cyan-300/30',
    accent: 'text-cyan-200',
    text: 'text-cyan-100',
    chip: 'bg-cyan-300/15 text-cyan-100 border border-cyan-300/30',
    progressFill: 'bg-gradient-to-r from-cyan-300 to-brand-200',
    glow: 'shadow-[0_0_60px_-15px_rgba(165,243,252,0.4)]',
    sectionBandRgb: '34,211,238',
  },
  diamond: {
    gradient: 'from-brand-400/25 via-brand-300/15 to-brand-200/25',
    border: 'border-brand-300/40',
    accent: 'text-brand-200',
    text: 'text-brand-100',
    chip: 'bg-brand-300/15 text-brand-100 border border-brand-300/40',
    progressFill: 'bg-gradient-to-r from-brand-300 via-brand-300 to-brand-200',
    glow: 'shadow-[0_0_80px_-15px_rgba(125,211,252,0.5)]',
    sectionBandRgb: '10, 18, 51',
  },
};

/**
 * Per-tier benefits, used to render the Rewards page tier matrix and the
 * "what you get" copy on Home / StatusProgressCard. Each benefit row is one
 * marketable line of value.
 */
export const TIER_BENEFITS = {
  member: [
    { key: 'earn',         label: 'Earn 2 miles per $1 spent' },
    { key: 'redeem',       label: 'Redeem miles for travel and 200+ gift cards' },
    { key: 'no_expire',    label: 'Miles never expire while account is active' },
    { key: 'concierge',    label: 'Concierge support during business hours' },
  ],
  silver: [
    { key: 'earn',         label: 'Earn 4 miles per $1 (2× base rate)' },
    { key: 'welcome',      label: '500 welcome bonus miles' },
    { key: 'birthday',     label: '250 bonus miles every birthday' },
    { key: 'concierge',    label: 'Concierge support during business hours' },
  ],
  gold: [
    { key: 'earn',         label: 'Earn 6 miles per $1 (3× base rate)' },
    { key: 'welcome',      label: '1,000 welcome bonus miles' },
    { key: 'birthday',     label: '500 bonus miles every birthday' },
    { key: 'bag',          label: 'Free checked bag for 1 passenger on first 3 bookings each year' },
    { key: 'starbucks',    label: '$10 Starbucks gift card on first 3 bookings each year' },
    { key: 'concierge',    label: 'Concierge support during business hours' },
  ],
  platinum: [
    { key: 'earn',         label: 'Earn 8 miles per $1 (4× base rate)' },
    { key: 'welcome',      label: '2,500 welcome bonus miles' },
    { key: 'birthday',     label: '1,000 bonus miles every birthday' },
    { key: 'bag',          label: 'Free checked bag for 1 passenger on first 3 bookings each year' },
    { key: 'seats',        label: 'Free Preferred Seats for member + 1 companion (up to 2 segments, $35/seat cap, first 3 bookings)' },
    { key: 'starbucks',    label: '$10 Starbucks gift card on first 3 bookings each year' },
    { key: 'uber',         label: '$50 Uber gift card on up to 3 bookings each year' },
    { key: 'concierge',    label: 'Priority concierge support — extended hours' },
  ],
  diamond: [
    { key: 'earn',         label: 'Earn 10 miles per $1 (5× base rate)' },
    { key: 'welcome',      label: '5,000 welcome bonus miles' },
    { key: 'birthday',     label: '2,500 bonus miles every birthday' },
    { key: 'bag',          label: 'Free checked bag for member + companion on first 5 bookings each year' },
    { key: 'seats',        label: 'Free Preferred Seats for member + 1 companion (up to 2 segments, $55/seat cap, first 5 bookings)' },
    { key: 'starbucks',    label: '$20 Starbucks gift card on first 5 bookings each year' },
    { key: 'uber',         label: '$75 Uber gift card on up to 3 bookings each year' },
    { key: 'concierge',    label: 'Priority concierge support — extended hours' },
  ],
};

/** Classify a USD spend total into a tier id. */
export function classifyTier(spend) {
  const s = Number(spend) || 0;
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (s >= TIERS[i].floor) return TIERS[i].id;
  }
  return 'member';
}

/** Get the tier definition for an id, with a safe fallback to Member. */
export function getTier(tierId) {
  return TIER_BY_ID[tierId] || TIER_BY_ID.member;
}

/**
 * Given a current spend total, return progress info to the next tier:
 *   { currentTier, nextTier, dollarsToNext, percentToNext }
 * If already at top tier, nextTier is null and percentToNext is 100.
 */
export function tierProgress(spend) {
  const s = Number(spend) || 0;
  const currentId = classifyTier(s);
  const currentIdx = TIERS.findIndex((t) => t.id === currentId);
  const currentTier = TIERS[currentIdx];
  const nextTier = TIERS[currentIdx + 1] || null;

  if (!nextTier) {
    return { currentTier, nextTier: null, dollarsToNext: 0, percentToNext: 100 };
  }
  const span = nextTier.floor - currentTier.floor;
  const into = s - currentTier.floor;
  const percentToNext = span > 0 ? Math.max(0, Math.min(100, (into / span) * 100)) : 0;
  return {
    currentTier,
    nextTier,
    dollarsToNext: Math.max(0, nextTier.floor - s),
    percentToNext,
  };
}

/** Founding-period launch dates. Status earned from 2024-01-01 → 2025-12-31
 *  is granted on launch and valid for the 2026 benefit year + January 2027 grace. */
export const FOUNDING_PERIOD = {
  earningStart: '2024-01-01',
  earningEnd: '2025-12-31',
  benefitStart: '2026-01-01',
  benefitEnd: '2027-01-31',
};

/** Passed to Postgres `defera_claim_status_benefit`; must mirror migration 050. */
export const STATUS_BENEFIT_CODES = /** @type {const} */ ([
  'checked_bag',
  'preferred_seat_segment',
  'starbucks_giftcard',
  'uber_giftcard',
]);

/**
 * Caps mirrored in migration 050. Enforced only via `/api/status/benefits/claim`
 * → `defera_claim_status_benefit`. Cap year defaults to LA local calendar year.
 */
export const STATUS_BENEFIT_CAPS = {
  checked_bag: {
    tiers: ['gold', 'platinum', 'diamond'],
    bookingsPerYear: { gold: 3, platinum: 3, diamond: 5 },
    faceUsdRequired: false,
  },
  preferred_seat_segment: {
    tiers: ['platinum', 'diamond'],
    bookingsPerYear: { platinum: 3, diamond: 5 },
    segmentsPerBooking: 2,
    perSeatUsdCap: { platinum: 35, diamond: 55 },
    faceUsdRequired: true,
  },
  starbucks_giftcard: {
    tiers: ['gold', 'platinum', 'diamond'],
    bookingsPerYear: { gold: 3, platinum: 3, diamond: 5 },
    perBookingUsdCap: { gold: 10, platinum: 10, diamond: 20 },
    annualUsdCap: { gold: 30, platinum: 30, diamond: 100 },
    faceUsdRequired: true,
  },
  uber_giftcard: {
    tiers: ['platinum', 'diamond'],
    bookingsPerYear: { platinum: 3, diamond: 3 },
    perBookingUsdCap: { platinum: 50, diamond: 75 },
    annualUsdCap: { platinum: 150, diamond: 225 },
    faceUsdRequired: true,
  },
};

/** Birthday miles (Silver+). DB sources DOB from `passengers` — see migration 050. */
export const TIER_BIRTHDAY_BONUS_MILES = {
  silver: 250,
  gold: 500,
  platinum: 1000,
  diamond: 2500,
};
